import React from "react";
//#region redux
import { AppState } from "store";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import {
  loadTodoNavigationItems,
  setTodoListNavigation,
  setTodoNavigationCurrentIndex,
} from "store/actions/todoNavigationActions";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CommonConstants, ServiceConstants, TodoConstants } from "utils/constants";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { TodoNavigationProps } from "models/propTypes/todoPropTypes";
//#endregion
//#region components
import { EntityPagination, useAPIRequestKeyRef } from "components/common";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useTodosFromState from "./useTodosFromState";
import useTodoReportFilterDefinition from "./forms/useReportFilterDefinition";
import { useTodoService } from "serviceHooks";

//#endregion
import { ServiceHelper, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import { TodoService } from "services";

const TodoNavigation: React.FC<TodoNavigationProps> = (props) => {
  const todoService = useTodoService();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { todoCount, items } = useTodosFromState();
  const todoNavigationState = useSelector((store: AppState) => store.todos.todoNavigationState);
  const {
    currentNavigationItems,
    indexToOpen,
    pageToOpen,
    numPerPage,
    totalItems,
    refreshRequestId,
    todoCanIncludeInNavigation,
    loadNext: loadNextCustom,
  } = todoNavigationState ?? {};

  const [loading, setLoading] = React.useState(false);
  const [nextPageItems, setNextPageItems] = React.useState<{
    page: number;
    items: WintSuperkollAPIModel.Todo[];
    total: number;
  }>();

  const urlParams = useParams<EntityIdParams>();
  const pageIndex = Number.parseInt(urlParams.id ?? "0");
  const searchParams = new URLSearchParams(location.search);

  const { filterQueryStateHandler } = useTodoReportFilterDefinition();

  const toNumericIds = (params: { id: string | number }[] | undefined) =>
    params
      ?.flatMap((param) => `${param.id}`.split(","))
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));

  const urgencyFilterParams = filterQueryStateHandler.urgency?.urlDecode(searchParams);
  const urgency = toNumericIds(urgencyFilterParams);

  const groupFilterParams = filterQueryStateHandler.groups?.urlDecode(searchParams);
  const group = toNumericIds(groupFilterParams);

  const subTypeFilterParams = filterQueryStateHandler.subTypes?.urlDecode(searchParams);
  const subTypes = toNumericIds(subTypeFilterParams);

  React.useEffect(() => {
    if (todoCount === undefined && window.location.href.includes("/Todo")) {
      const numPerPage = TodoConstants.TodosPerPage;
      const todoCurrentIndex = pageIndex % (numPerPage ?? 0);
      const page = Math.floor(pageIndex / numPerPage);

      dispatch(
        setTodoListNavigation({
          indexToOpen: todoCurrentIndex,
          pageToOpen: page,
          numPerPage: numPerPage,
          todoCanIncludeInNavigation: (todo: WintSuperkollAPIModel.Todo) =>
            urgency?.length
              ? urgency.includes(todo.urgency)
              : group?.length
                ? group.includes(todo.group) && (subTypes === undefined || subTypes.includes(todo.subType))
                : true,
        })
      );
      navigate(`${UrlHelper.formatPath(`/Todo/${pageIndex}`)}?${searchParams.toString()}`, {
        state: { background: location },
        replace: window.location.href.includes("/Todo"),
      });
    }
  }, [todoCount]);

  const navigateOutOfTodoView = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(`${UrlHelper.formatPath("")}`, {
        state: { background: location },
        replace: true,
      });
    }
  };

  React.useEffect(() => {
    if (totalItems === 0 && todoCount !== undefined && window.location.href.includes("/Todo")) {
      setTimeout(() => {
        navigateOutOfTodoView();
      }, CommonConstants.SuccessIndicatorDurationMs);
    }
  }, [totalItems]);

  const goToItem = (index: number) => {
    navigate(`${UrlHelper.formatPath(`/Todo/${index}?${searchParams.toString()}`)}`, {
      state: { background: location },
      replace: true,
    });
  };

  const navigateToEntity = (index: number, page: number) => {
    dispatch(setTodoNavigationCurrentIndex(index, page));
    goToItem(page * (numPerPage ?? 0) + index);
  };

  const updateCurrent = (index: number, page: number) => {
    if (currentNavigationItems !== undefined) {
      const pageIndex = page * (numPerPage ?? 0) + index;
      if (
        pageToOpen !== page ||
        (totalItems !== undefined && pageIndex <= totalItems && currentNavigationItems[index] === undefined)
      ) {
        reloadItems(index, page);
      } else {
        if (totalItems !== undefined && pageIndex > totalItems && numPerPage !== undefined) {
          const indexBefore = pageIndex - 1;
          navigateToEntity(indexBefore % numPerPage, Math.floor(indexBefore / numPerPage));
        }
        navigateToEntity(index, page);
      }
    } else {
      reloadItems(index, page);
    }
  };

  const hasUrlFilter = "size" in searchParams && searchParams.size;

  const loadTodosByUrlFilter = (page: number) => {
    const filter = {
      groups: filterQueryStateHandler.groups?.urlDecode(searchParams),
      subTypes: filterQueryStateHandler.subTypes?.urlDecode(searchParams),
      urgency: filterQueryStateHandler.urgency?.urlDecode(searchParams),
      snoozedOnly: filterQueryStateHandler.snoozedOnly?.urlDecode(searchParams),
    };

    return TodoService.getTodosByFilter({
      ...ServiceConstants.DefaultFilter,
      ...todoService.reportToAPIFilter(filter),
      types: TodoConstants.SupportingTodoTypes,
      page: page,
      numPerPage: TodoConstants.TodosPerPage,
    }).then((res) => res.result.data);
  };

  const loadNext = async (page: number) => {
    if (loadNextCustom) {
      return loadNextCustom(page);
    } else if (hasUrlFilter) {
      return loadTodosByUrlFilter(page);
    } else {
      if (page === 0 && todoCount !== undefined) {
        return { items: items, totalItems: todoCount ?? 0 };
      } else {
        return TodoService.getTodos(page);
      }
    }
  };

  const refreshFromServer = async (page: number) => {
    if (hasUrlFilter && todoCanIncludeInNavigation) {
      return loadTodosByUrlFilter(page);
    }
    return loadNext(page);
  };

  const { apiRequestKeyRef, updateKey } = useAPIRequestKeyRef();

  const reloadItems = (index: number, page: number) => {
    if (nextPageItems?.page === page) {
      dispatch(loadTodoNavigationItems(nextPageItems.items, nextPageItems.total));
      navigateToEntity(index, page);
    } else {
      setLoading(true);
      const currentKey = updateKey();
      ServiceHelper.withAPIRequestKey(loadNext(page), currentKey).then((results) => {
        if (apiRequestKeyRef.current === results.key) {
          const items = results.data.items ?? [];
          dispatch(loadTodoNavigationItems(items, results.data.totalItems));
          navigateToEntity(index, page);
          setLoading(false);
        }
      });
    }
  };

  React.useEffect(() => {
    if (!refreshRequestId) {
      return;
    }
    const page = pageToOpen ?? 0;
    const currentKey = updateKey();
    ServiceHelper.withAPIRequestKey(refreshFromServer(page), currentKey).then((results) => {
      if (apiRequestKeyRef.current === results.key) {
        setNextPageItems(undefined);
        dispatch(loadTodoNavigationItems(results.data.items ?? [], results.data.totalItems));
      }
    });
  }, [refreshRequestId]);

  const onBeforePageEnd = (nextPage: number) => {
    if (nextPage === pageToOpen) return;
    setLoading(true);
    loadNext(nextPage).then((results) => {
      setNextPageItems({ items: results.items ?? [], total: results.totalItems, page: nextPage });
      setLoading(false);
    });
  };

  return (
    <EntityPagination
      total={totalItems}
      onCurrentIndexUpdate={updateCurrent}
      initialIndex={indexToOpen}
      navigationText={""}
      initialPage={pageToOpen}
      numPerPage={numPerPage}
      loadingNext={loading}
      onBeforePageEnd={onBeforePageEnd}
      onBeforeNavigation={props.onBeforeNavigation}
      navigationDelay={props.navigationDelay}
      className={cssExports.fadeIn}
    />
  );
};

export default TodoNavigation;
